import type { CSSProperties } from "react";
import Tooltip from "../../ui/kit-mdtw/tooltip/Tooltip";

/** Campos A/B y de postulación por etapas que trae cada fila del listado. */
export interface IExperimentoPostulacion {
  stageMode?: string | null;
  stageStatus?: string | null;
  completedStages?: number | null;
  totalStages?: number | null;
  experimentCode?: string | null;
  variantCode?: string | null;
  variantName?: string | null;
  isControlVariant?: boolean | null;
  forcedVariant?: boolean | null;
  invitationStatus?: string | null;
  invitationExpiresAt?: string | null;
  invitationOpenedAt?: string | null;
  invitationMessageStatus?: string | null;
  invitationScheduledAt?: string | null;
  invitationSentAt?: string | null;
  invitationError?: string | null;
}

interface Pill {
  /** Código estable del estado (el mismo con que filtra el backend). */
  code?: string;
  label: string;
  detail?: string;
  tooltip: string;
  color: string;
  bg: string;
}

const NEUTRAL = { color: "#64748b", bg: "#f1f5f9" };
const INFO = { color: "#1d4ed8", bg: "#dbeafe" };
const VARIANT_B = { color: "#7c3aed", bg: "#ede9fe" };
const PENDING = { color: "#92400e", bg: "#fef3c7" };
const SUCCESS = { color: "#047857", bg: "#d1fae5" };
const DANGER = { color: "#b91c1c", bg: "#fee2e2" };

const shortDateTime = (iso?: string | null): string | undefined => {
  if (!iso) return undefined;
  const date = new Date(iso);
  if (isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat("es-CL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
};

/** "A" para la variante de control y "B" para la otra; null si no participó de un experimento. */
export const resolveVariantPill = (item: IExperimentoPostulacion): Pill | null => {
  if (!item.variantCode) return null;
  const isControl = item.isControlVariant === true;
  const letter = isControl ? "A" : "B";
  const name = item.variantName || item.variantCode;
  const forced = item.forcedVariant === true;
  return {
    label: `${letter} · ${item.stageMode === "ETAPAS" ? "Por etapas" : "Completo"}`,
    detail: forced ? "QA" : undefined,
    tooltip: `Experimento ${item.experimentCode ?? "—"} · Variante ${letter}: ${name}`
      + (forced ? ". Asignada a mano para QA (no cuenta en las métricas)." : ""),
    ...(isControl ? INFO : VARIANT_B),
  };
};

/** En qué paso va: "Paso 1 de 2" mientras está parcial, "Completa 2/2" al terminar. */
export const resolveStagePill = (item: IExperimentoPostulacion): Pill | null => {
  if (item.stageMode !== "ETAPAS") return null;
  const total = item.totalStages ?? 2;
  const done = item.completedStages ?? 1;
  if (item.stageStatus === "COMPLETA") {
    return { label: `Completa ${total}/${total}`, tooltip: "Completó todas las etapas del formulario", ...SUCCESS };
  }
  return { label: `Paso ${Math.min(done + 1, total)} de ${total}`, tooltip: `Envió la etapa ${done}; falta completar la etapa ${done + 1}`, ...PENDING };
};

/**
 * Estados de la columna "WhatsApp 2ª parte" con que se filtra la tabla. Los
 * códigos son los de STAGE_WHATSAPP_SQL en el backend (applicationFilter.ts):
 * si cambia una regla, cambiar la otra.
 */
export const STAGE_WHATSAPP_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "PROGRAMADO", label: "Programado" },
  { value: "ENVIANDO", label: "Enviando" },
  { value: "ENVIADO", label: "Enviado (sin abrir)" },
  { value: "ABRIO", label: "Enviado · abrió" },
  { value: "COMPLETO", label: "Completó" },
  { value: "VENCIDO", label: "Enlace vencido" },
  { value: "ERROR", label: "Error al enviar" },
  { value: "CANCELADO", label: "Cancelado" },
  { value: "SIN_INVITACION", label: "Sin invitación" },
  { value: "NO_NECESARIO", label: "No necesario" },
  { value: "NO_APLICA", label: "Sin etapas (—)" },
];

export const AB_VARIANT_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "A", label: "A · control" },
  { value: "B", label: "B · variante" },
  { value: "SIN_EXPERIMENTO", label: "Sin experimento (—)" },
];

export const FORM_STAGE_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: "PENDIENTE", label: "Falta una etapa (Paso N de M)" },
  { value: "COMPLETA", label: "Completa" },
  { value: "NO_APLICA", label: "Sin etapas (—)" },
];

const LIVE_INVITATION_STATUSES = new Set(["PROGRAMADA", "ENVIADA", "ABIERTA"]);

/** El enlace venció: por estado (reconciliador) o por fecha, aunque aún no se marque EXPIRADA. */
const isInvitationExpired = (item: IExperimentoPostulacion, now: number): boolean => {
  if (item.invitationStatus === "EXPIRADA") return true;
  if (!item.invitationStatus || !LIVE_INVITATION_STATUSES.has(item.invitationStatus) || !item.invitationExpiresAt) return false;
  const expiresAt = new Date(item.invitationExpiresAt).getTime();
  return !isNaN(expiresAt) && expiresAt <= now;
};

/**
 * Estado del WhatsApp con el enlace a la segunda parte del formulario. Las
 * fechas (programado / enviado / vence) van solo en el tooltip, no bajo el chip.
 */
export const resolveWhatsappPill = (item: IExperimentoPostulacion, now: number = Date.now()): Pill | null => {
  if (item.stageMode !== "ETAPAS") return null;
  if (!item.invitationStatus) {
    return item.stageStatus === "COMPLETA"
      ? { code: "NO_NECESARIO", label: "No necesario", tooltip: "Completó todo en la primera etapa: no hacía falta enviar el enlace", ...NEUTRAL }
      : { code: "SIN_INVITACION", label: "Sin invitación", tooltip: "Aún no se crea la invitación (el reconciliador la crea en unos minutos)", ...PENDING };
  }
  const sentAt = shortDateTime(item.invitationSentAt);
  const expiresAt = shortDateTime(item.invitationExpiresAt);
  if (item.invitationStatus === "COMPLETADA") {
    return { code: "COMPLETO", label: "Completó", tooltip: `Completó la segunda parte desde el enlace${sentAt ? ` (enviado el ${sentAt})` : ""}`, ...SUCCESS };
  }
  if (isInvitationExpired(item, now)) {
    return {
      code: "VENCIDO",
      label: "Enlace vencido",
      tooltip: `El enlace venció${expiresAt ? ` el ${expiresAt}` : ""} sin que completara la segunda parte${sentAt ? ` (enviado el ${sentAt})` : ""}`,
      ...DANGER,
    };
  }
  if (item.invitationStatus === "CANCELADA") {
    return { code: "CANCELADO", label: "Cancelado", tooltip: "La invitación se canceló (completó antes, se reemplazó o se inhabilitó)", ...NEUTRAL };
  }
  const expiresText = expiresAt
    ? `. El enlace vence el ${expiresAt}`
    : ". El enlace no vence: sirve hasta que complete la segunda parte";
  switch (item.invitationMessageStatus) {
    case "PROGRAMADO": {
      const at = shortDateTime(item.invitationScheduledAt);
      return { code: "PROGRAMADO", label: "Programado", tooltip: `El WhatsApp sale ${at ? `el ${at}` : "en unos minutos"}${expiresText}`, ...PENDING };
    }
    case "ENVIANDO":
      return { code: "ENVIANDO", label: "Enviando…", tooltip: "Entregado al servicio de mensajería, esperando respuesta de WhatsApp", ...PENDING };
    case "ERROR":
      return { code: "ERROR", label: "Error al enviar", tooltip: item.invitationError || "WhatsApp rechazó el envío", ...DANGER };
    case "CANCELADO":
      return { code: "CANCELADO", label: "Cancelado", tooltip: "El envío se canceló", ...NEUTRAL };
    case "ENVIADO":
    case "ENTREGADO":
    case "LEIDO": {
      const opened = item.invitationStatus === "ABIERTA" || Boolean(item.invitationOpenedAt);
      return {
        code: opened ? "ABRIO" : "ENVIADO",
        label: opened ? "Enviado · abrió" : "Enviado",
        tooltip: `WhatsApp enviado${sentAt ? ` el ${sentAt}` : ""}${opened ? " y la persona abrió el enlace" : "; aún no abre el enlace"}${expiresText}`,
        ...(opened ? SUCCESS : INFO),
      };
    }
    default:
      return { label: item.invitationStatus, tooltip: `Invitación ${item.invitationStatus}`, ...NEUTRAL };
  }
};

const PillCell = ({ pill }: { pill: Pill | null }) => {
  if (!pill) return <span style={{ color: "#94a3b8" }}>—</span>;
  return (
    <Tooltip text={pill.tooltip} textTransform="none">
      <span style={{ display: "inline-flex", flexDirection: "column", gap: "1px", cursor: "help" }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            padding: "3px 9px",
            borderRadius: "12px",
            background: pill.bg,
            color: pill.color,
            border: `1px solid ${pill.color}22`,
            fontFamily: "Roboto, sans-serif",
            fontSize: "11.5px",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {pill.label}
        </span>
        {pill.detail && (
          <span style={{ fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: "11px", color: "#94a3b8", paddingLeft: "4px" }}>
            {pill.detail}
          </span>
        )}
      </span>
    </Tooltip>
  );
};

export const VarianteABCell = ({ item }: { item: IExperimentoPostulacion }) => <PillCell pill={resolveVariantPill(item)} />;
export const EtapaFormularioCell = ({ item }: { item: IExperimentoPostulacion }) => <PillCell pill={resolveStagePill(item)} />;
interface WhatsappEtapaCellProps {
  item: IExperimentoPostulacion;
  /** "Enviar ahora": solo se ofrece si el WhatsApp está programado. */
  onSendNow?: () => void;
  /** "Reactivar enlace": solo se ofrece si el enlace venció (queda sin vencimiento). */
  onExtend?: () => void;
  /** Hay una acción en curso para esta fila. */
  busy?: boolean;
}

const actionButtonStyle = (color: string, busy: boolean): CSSProperties => ({
  padding: "2px 8px",
  borderRadius: "10px",
  border: `1px solid ${color}55`,
  background: "#fff",
  color,
  fontFamily: "Roboto, sans-serif",
  fontSize: "11px",
  fontWeight: 600,
  whiteSpace: "nowrap",
  cursor: busy ? "wait" : "pointer",
  opacity: busy ? 0.6 : 1,
});

export const WhatsappEtapaCell = ({ item, onSendNow, onExtend, busy = false }: WhatsappEtapaCellProps) => {
  const pill = resolveWhatsappPill(item);
  const action = pill?.code === "PROGRAMADO" && onSendNow
    ? { label: "Enviar ahora", tooltip: "Envía el WhatsApp ahora, sin esperar la hora programada. Queda registrado con tu usuario.", onClick: onSendNow, color: "#1d4ed8" }
    : pill?.code === "VENCIDO" && onExtend
      ? { label: "Reactivar enlace", tooltip: "Reactiva el mismo enlace que ya recibió la persona, sin vencimiento: sirve hasta que complete la segunda parte. Queda registrado con tu usuario.", onClick: onExtend, color: "#b91c1c" }
      : null;
  if (!action) return <PillCell pill={pill} />;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
      <PillCell pill={pill} />
      <Tooltip text={action.tooltip} textTransform="none">
        <button
          type="button"
          disabled={busy}
          aria-label={action.label}
          onClick={(event) => {
            event.stopPropagation();
            if (!busy) action.onClick();
          }}
          style={actionButtonStyle(action.color, busy)}
        >
          {busy ? "…" : action.label}
        </button>
      </Tooltip>
    </span>
  );
};
